/*
 * Ice Blue shell layer — the first shared slice of the Ice Blue design used
 * OUTSIDE the encounter (owner-locked 2026-08-24: "tokens as needed", no full
 * design system yet). Scope: the in-app workspace tab strip and the keep-alive
 * tab pane. The --ibs-* tokens are a deliberate subset of the encounter's
 * --ib-* palette under shell-scoped names; grow this file only as the shell
 * needs more, and fold both into a real token package when the Ice Blue look
 * graduates to the rest of the site.
 */

.ib-workspace-toolbar,
.ib-shell-tabs,
.ib-confirm-scrim {
  --ibs-accent: #3d8bf5;
  --ibs-accent-deep: #2468cc;
  --ibs-panel: #ffffff;
  --ibs-desk: #fafcff;
  --ibs-line: #e1ecf8;
  --ibs-line-strong: #cde1f6;
  --ibs-wash: #e3f0fd;
  --ibs-wash-soft: #f0f7fe;
  --ibs-ink: #1f2733;
  --ibs-ink-2: #55606e;
  --ibs-ink-3: #8a94a2;
  --ibs-warn: #b3722a;
  /* Filled buttons: white on accent-deep (5.1:1); the lighter accent fails contrast. */
  --ibs-btn-fill: #2468cc;
  --ibs-btn-fill-hover: #1b4f9e;
  --ibs-btn-ink: #ffffff;
}

.ib-workspace-toolbar {
  display: flex;
  flex: 0 0 40px;
  min-width: 0;
  height: 40px;
  background: var(--ibs-desk);
  border-bottom: 1px solid var(--ibs-line);
}
.ib-workspace-back {
  flex: 0 0 34px;
  display: grid;
  place-items: center;
  width: 34px;
  height: 32px;
  margin: 4px;
  padding: 0;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--ibs-ink-2);
  cursor: pointer;
}
.ib-workspace-back svg {
  transform: rotate(180deg);
}
.ib-workspace-back:hover:not(:disabled) {
  background: var(--ibs-wash);
}
.ib-workspace-back:disabled {
  opacity: 0.35;
  cursor: default;
}
.ib-workspace-back:focus-visible {
  outline: 2px solid var(--ibs-accent);
  outline-offset: -2px;
}

.ib-shell-tabs {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: flex-end;
  gap: 4px;
  height: 40px;
  padding: 6px 10px 0;
  background: var(--ibs-desk);
  border-bottom: 1px solid var(--ibs-line);
  overflow-x: auto;
  scrollbar-width: thin;
  font-family: "Inter", "SF Pro Text", "Segoe UI", system-ui, sans-serif;
}

.ib-shell-tab {
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
  max-width: 230px;
  height: 34px;
  border: 1px solid transparent;
  border-bottom: none;
  border-radius: 9px 9px 0 0;
  color: var(--ibs-ink-2);
  position: relative;
}
.ib-shell-tab:hover {
  background: var(--ibs-wash-soft);
  color: var(--ibs-ink);
}
.ib-shell-tab.active {
  background: var(--ibs-panel);
  border-color: var(--ibs-line-strong);
  color: var(--ibs-ink);
}
/* Hide the strip's bottom border under the active tab so it reads as
   connected to the page below. */
.ib-shell-tab.active::after {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  bottom: -1px;
  height: 1px;
  background: var(--ibs-panel);
}

.ib-shell-tab-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  height: 100%;
  padding: 0 12px;
  border: none;
  background: transparent;
  color: inherit;
  font: inherit;
  font-size: 12.5px;
  font-weight: 500;
  white-space: nowrap;
  cursor: pointer;
}
.ib-shell-tab.active .ib-shell-tab-btn {
  font-weight: 600;
}
/* A closable tab's label button hands its right padding to the close button. */
.ib-shell-tab-btn:not(:last-child) {
  padding-right: 4px;
}

.ib-shell-tab-label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ib-shell-tab-dot {
  flex: 0 0 auto;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--ibs-accent);
}

.ib-shell-tab-close {
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  /* 24px hit area meets the WCAG 2.5.8 target-size minimum; the glyph stays
     12px (see the svg rule below), centered in the larger transparent box. */
  width: 24px;
  height: 24px;
  margin-right: 4px;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: var(--ibs-ink-3);
  cursor: pointer;
}
.ib-shell-tab-close:hover {
  background: var(--ibs-wash);
  color: var(--ibs-ink);
}
/* Counter the global styles.css `svg { width/height: 20px }` rule — a 20px
   glyph overflows this 18px button and degrades centering. */
.ib-shell-tab-close svg {
  display: block;
  width: 12px;
  height: 12px;
}

/* Keep-alive tab pane: the synthetic encounter stays MOUNTED (hidden, never
   unmounted) while another tab fronts, so dictation and reasoning keep
   running. Budget for the 40px strip and mobile bottom navigation so the
   encounter fills the available viewport without covered controls. */
.ib-tab-pane {
  height: calc(100dvh - 40px - var(--shell-bottom-nav-height, 0px));
  min-height: 0;
}
/* Hard-viewport pages (height: 100dvh + overflow hidden) must budget for the
   strip too, or its 40px clips their bottom edge. Matches only when the strip
   is actually rendered — persisted mode has no strip and is untouched. */
.main-column:has(> .ib-workspace-toolbar) .horizon-vault-workspace {
  height: calc(100dvh - 40px - var(--shell-bottom-nav-height, 0px));
}

/* ---- Encounter-switch confirm ------------------------------------------ */
/* Shell-level modal: opening a visit that would replace the keep-alive
   encounter tab asks first. Above every encounter overlay (PDF backdrop is
   z-index 100). */
.ib-confirm-scrim {
  position: fixed;
  inset: 0;
  z-index: 130;
  display: grid;
  place-items: center;
  padding: 20px;
  background: rgba(31, 39, 51, 0.32);
  backdrop-filter: blur(4px);
  font-family: "Inter", "SF Pro Text", "Segoe UI", system-ui, sans-serif;
}
.ib-confirm {
  width: min(440px, 100%);
  padding: 22px 24px 20px;
  border: 1px solid var(--ibs-line-strong);
  border-radius: 16px;
  background: var(--ibs-panel);
  box-shadow:
    0 24px 60px rgba(36, 104, 204, 0.2),
    0 6px 16px rgba(31, 39, 51, 0.12);
  color: var(--ibs-ink);
}
.ib-confirm-title {
  display: flex;
  align-items: center;
  gap: 9px;
  margin: 0 0 8px;
  font-size: 15.5px;
  font-weight: 650;
}
.ib-confirm-title svg {
  width: 16px;
  height: 16px;
  color: var(--ibs-warn);
  flex: 0 0 auto;
}
.ib-confirm-detail {
  margin: 0 0 18px;
  font-size: 13px;
  line-height: 1.55;
  color: var(--ibs-ink-2);
}
.ib-confirm-detail :is(p, ul) {
  margin: 0;
}
.ib-confirm-detail > * + * {
  margin-top: 6px;
}
.ib-confirm-detail ul {
  padding-left: 18px;
  color: var(--ibs-ink);
}
.ib-confirm-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
.ib-confirm-btn {
  min-height: 36px;
  padding: 0 15px;
  border-radius: 10px;
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: none;
}
.ib-confirm-btn.ghost {
  border: 1px solid var(--ibs-line-strong);
  background: var(--ibs-panel);
  color: var(--ibs-ink);
}
.ib-confirm-btn.ghost:hover {
  background: var(--ibs-wash-soft);
}
.ib-confirm-btn.primary {
  background: var(--ibs-btn-fill);
  color: var(--ibs-btn-ink);
}
.ib-confirm-btn.primary:hover {
  background: var(--ibs-btn-fill-hover);
}
/* Destructive confirm (alert values from docs/UI-GUIDE.md); white text on #b42318 is 6.5:1. */
.ib-confirm-btn.danger {
  background: #b42318;
  color: #fff;
}
.ib-confirm-btn.danger:hover {
  background: #912018;
}
.ib-confirm-btn:focus-visible {
  outline: 2px solid var(--ibs-accent);
  outline-offset: 2px;
}
.ib-tab-pane.hidden {
  display: none;
}
.ib-tab-pane .ib-encounter {
  height: 100%;
}

.ib-microphone-owner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 20px;
  background: rgba(50, 113, 170, 0.08);
  border-bottom: 1px solid rgba(50, 113, 170, 0.16);
  color: #315a77;
  font-size: 12px;
}
.ib-microphone-owner button {
  flex-shrink: 0;
  border: 1px solid #adc4d4;
  border-radius: 8px;
  padding: 6px 10px;
  background: #fff;
  color: inherit;
  cursor: pointer;
}
@media (max-width: 640px) {
  .ib-microphone-owner {
    flex-wrap: wrap;
    padding: 8px 12px;
  }
}
